import React, { useEffect, useRef, useState } from "react";
import { timeSince } from "../../utility";
import { INotification, TRecord } from "../../types/common";
import ButtonsWithBadge from "../ButtonsWithBadge";

const CONVERSATION_LIST_PATH = "/bl/api/process-api/social-media/get-conversation-list";
const X_API_ENDPOINT = "U2FsdGVkX19Vv86AW7zq9i/KcoZ5D4HcGKDjTOWNWajjpPSEYaB/UyYr65QADpe8udrOPv2lbLCD3V1aYLx+VhhjDSHZD+LcIQGvWivDir4cR/oQXZh0HH4AHI8OQZadZ3JCAMcV9ueAnIJiW0GftNkQ+3Kh/eosyTAtttopts0I7ydHp7roV2r9c3CaQN6ywfBwHHOo6wESg1NroNQEUx9N8jx3BwD7VLXaAWNSIk+gmNJvbwMLlqLOBh6n5d1xgRT+MspmeHhWz8SCTAVbZTYBNGAog9mouql2qKgtgPcdsHrRdgAuVpK2mM0kVFhjJMCZ0jhUTNU9LBcgi0snmqCKbBWjg6q2xg8314czInM=";

interface IConversationSummary {
  id: number | string;
  unreadCount: number;
}

interface IConversationListResponse {
  data: IConversationSummary[];
  statusCode: number;
}

interface IProps {
  userData?: TRecord | null;
}

async function fetchConversationList(configId: number): Promise<IConversationSummary[]> {
  const userSession = window.__USER_SESSION__;

  const response = await fetch(`${window.location.origin}${CONVERSATION_LIST_PATH}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-api-endpoint": X_API_ENDPOINT,
    },
    body: JSON.stringify({
      senderId: userSession.id,
      senderTableName: "users",
      senderType: "user",
      configId,
      entity: "",
      entityId: 0,
    }),
  });

  if (!response.ok) throw new Error(`get-conversation-list failed: ${response.status}`);

  const json: IConversationListResponse = await response.json();
  return json.data ?? [];
}

const Index: React.FC<IProps> = ({ userData }) => {
  const configId = Number(userData?.configId) || 1;
  const [ready, setReady] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [latestNotification, setLatestNotification] = useState<INotification | null>(null);

  // Refs so event handlers always see fresh data without re-subscribing
  const unreadMapRef = useRef<Record<string, number>>({}); // conversationId → unreadCount
  const messageIdMapRef = useRef<Record<string, string>>({}); // conversationId → latest messageId

  const syncCount = (map: Record<string, number>) => {
    setTotalUnreadCount(Math.max(0, Object.values(map).reduce((a, b) => a + b, 0)));
  };

  // Populate initial unread counts from conversation list API on mount
  useEffect(() => {
    fetchConversationList(configId)
      .then((conversations) => {
        const map: Record<string, number> = {};
        conversations.forEach(({ id, unreadCount }) => {
          if (unreadCount > 0) map[String(id)] = unreadCount;
        });
        unreadMapRef.current = map;
        syncCount(map);
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [configId]);

  // Subscribe to J36 notification events once
  useEffect(() => {
    const J36 = (window as any)?.J36;
    if (!J36?.Notification) return;

    const handleNotification = (event: TRecord, messageId: string) => {
      const timestamp = event.timestamp || Number(messageId?.split("-")[0]);
      if (timeSince(timestamp).toLowerCase() === "invalid date") return;

      const data: TRecord = event?.data ?? {};
      if (data.type !== "SOCIAL_MEDIA") return;

      const conversationId = String(
        data.conversationId ?? data.message?.conversationId ?? messageId
      );

      // Prefer API-provided unreadCount; fallback to incrementing current
      const prevCount = unreadMapRef.current[conversationId] ?? 0;
      const newCount = data.unreadCount != null ? Number(data.unreadCount) : prevCount + 1;

      unreadMapRef.current = { ...unreadMapRef.current, [conversationId]: newCount };
      messageIdMapRef.current = { ...messageIdMapRef.current, [conversationId]: messageId };
      syncCount(unreadMapRef.current);

      const label = data.label || data.title || "New Notification";
      setLatestNotification({
        messageId,
        ...data?.message,
        ...data,
        label,
        timestamp,
        conversationId,
        unreadCount: newCount,
      });
    };

    // Fired by the messaging app after create-last-read-log succeeds
    // Expected payload: { conversationId: string | number }
    const handleMessageRead = (event: TRecord) => {
      const conversationId = String(
        event?.conversationId ?? event?.data?.conversationId ?? ""
      );
      if (!conversationId) return;

      const map = { ...unreadMapRef.current };
      delete map[conversationId];
      unreadMapRef.current = map;
      syncCount(map);

      const clearedMessageId = messageIdMapRef.current[conversationId];
      if (clearedMessageId && J36?.Notification) {
        J36.Notification.DeleteNotification(clearedMessageId);
      }
      delete messageIdMapRef.current[conversationId];

      setLatestNotification((prev) =>
        prev?.conversationId === conversationId ? null : prev
      );
    };

    J36.Notification.Subscribe("HEADER_BELL_NOTIFICATION", handleNotification);
    J36.Notification.Subscribe("SOCIAL_MEDIA_READ", handleMessageRead);

    return () => {
      J36.Notification.Unsubscribe("HEADER_BELL_NOTIFICATION", handleNotification);
      J36.Notification.Unsubscribe("SOCIAL_MEDIA_READ", handleMessageRead);
    };
  }, []); // subscribe once — handlers read fresh data via refs

  const handleClick = () => {
    const notification = userData ?? latestNotification;
    if (!notification) return;

    const redirectionType = notification.redirectionType || "page";
    const pageId = notification.redirectionId || "68c970f70df8f087b47353a8";
    const params = notification.params || [];
    const popup = notification.popup ?? true;

    const appMgr = (window as any)?.getAppWindow?.() || window;
    appMgr.navigateApp(redirectionType, pageId, params, { popup });
  };

  return (
    <ButtonsWithBadge
      count={ready ? totalUnreadCount : 0}
      onClick={handleClick}
    />
  );
};

export default Index;
